/**
 * The register verb engine (13-20): triage, reportability, WHS, notification and handling on all five registers as
 * one pure function, so the audit contract is proven once, without SharePoint.
 *
 * `buildRecordVerb(verb, row, actor, input, now)` → `{ ok: true, patch, siblings, warnings }` or
 * `{ ok: false, refusals, warnings }`. Shaped like `lib/actionVerbs.ts` (13-04): the PATCH holds only the columns
 * that change, and there is one `StateChange` sibling draft per changed column (FIELDSET §1.7). Rows the caller adds
 * (`ActionTaken`, `InvestigationStep`, `Note` with a §3.4.1 `LogKind`) follow them.
 *
 * Driven by 13-17's field set (`config/fieldset/`): each verb's column list (`verbColumns`, never retyped), the
 * kinds, caps, required rules and live choices, and the §3.4.1 LogKind map. Values go through 13-18's
 * `lib/fieldValues`. The only literals here are what FIELDSET or the plan state and the field set does not carry:
 * - `VERB_STAMPS` (the columns a verb sets itself) and the triage `Status` rule (`New` → `Actioning`);
 * - `WRITER_OF_VERB` (FIELDSET §4.0's writer codes, which the §3.4.1 map is keyed by);
 * - `UNDETERMINED` (§1.1 / §1.3: the SharePoint defaults that mean "not decided yet") and `KEPT_COLUMNS` (§2.1
 *   `AwareAt`: "later verbs keep it"). See D-13-20-*.
 *
 * Not here: permissions (13-22 `recordAuthority.ts`, as `actionVerbs` / `actionAuthority` split), closure verbs
 * (13-21 builds them on `diffRecord` / `draftStateChanges` / `finaliseDrafts`), I/O (13-23). Pure: no Graph, no React,
 * no `Date.now()`; `now` and `input.auditId` come from the caller. A programming error (an unknown verb, a verb the
 * register lacks, a missing audit id) throws `RecordVerbError`; everything a person can get wrong is a refusal, and
 * every refusal comes back in one pass.
 */

import { liveChoice } from '../config/fieldset/live';
import { RECORD_REGISTERS, STAGE_OF_VERB, fieldOf, verbColumns, verbsOf, choicesOf, type FieldSpec } from '../config/fieldset/registerFields';
import { ENTRY_TYPES, ENTRY_TYPE_MAP, LOG_KINDS, LOG_KIND_MAP, type SiblingWriter } from '../config/fieldset/siblings';
import type { AuditEntryType } from '../types/qms';
import type {
  RecordFieldValue,
  RecordPatch,
  RecordRegister,
  RecordRow,
  RecordVerbKey,
  Refusal,
  RequiredRule,
  SiblingDraft,
  VerbResult,
} from '../types/records';
import {
  TEXT_CAPS,
  checkChoice,
  checkUpn,
  classifyInstant,
  cleanText,
  dayExisting,
  dayNew,
  melbourneDayOf,
  melbourneWallToInstant,
  notInFuture,
  toInstant,
} from './fieldValues';
import {
  cleanLodgeValue,
  contactWarning,
  describeRule,
  lodgeConditions,
  looksLikeContact,
  reportedBeforeOccurred,
  type LodgeActor,
  type WallTime,
} from './lodgeBuilder';
import { NOT_REPORTABLE_DECISION, REPORTABLE_DECISIONS, notificationDue, overrideDue } from './notificationDeadlines';
import { actionTag } from './outbox';
import { missingLodgeColumns } from './reportCompleteness';

// ---------------------------------------------------------------------------
// Errors and types
// ---------------------------------------------------------------------------

export type RecordVerbErrorCode = 'unknown-verb' | 'verb-not-on-register' | 'audit-id-required';

/** A programming error (not a person's mistake). `code` is stable; the message is for developers. */
export class RecordVerbError extends Error {
  readonly code: RecordVerbErrorCode;

  constructor(code: RecordVerbErrorCode, message?: string) {
    super(message ?? code);
    this.name = 'RecordVerbError';
    this.code = code;
  }
}

/** The refusal codes this engine returns (`Refusal.code`). */
export type RecordRefusalCode =
  | 'invalid-actor'
  | 'not-this-verb'
  | 'stamped-column'
  | 'status-via-closure'
  | 'not-writable'
  | 'not-text'
  | 'too-long'
  | 'unknown-choice'
  | 'invalid-upn'
  | 'guest-upn'
  | 'position-not-person'
  | 'invalid-date'
  | 'invalid-day'
  | 'invalid-time'
  | 'nonexistent-time'
  | 'in-future'
  | 'clear-needs-reason'
  | 'change-needs-reason'
  | 'required'
  | 'entrytype-not-allowed'
  | 'logkind-required'
  | 'logkind-not-allowed'
  | 'not-applicable'
  | 'contact-in-note'
  | 'no-change'
  | 'reason-required'
  | 'computed-column'
  | 'no-deadline'
  // 13.2-03: complete (D-13.2-P-3) and the triage report gate (D-13.2-P-4)
  | 'record-closed'
  | 'provenance-locked'
  | 'reported-on-locked'
  | 'cannot-blank-required'
  | 'clear-only'
  | 'before-occurred'
  | 'report-incomplete';

/**
 * The verbs this engine builds (13-20), plus 13.2-03's `complete` (D-13.2-P-3: the CM completes an incident's report
 * after lodge, intake stubs included). The closure verbs are 13-21's.
 */
export type EngineVerb = Extract<RecordVerbKey, 'complete' | 'triage' | 'reportability' | 'whs' | 'notification' | 'handling'>;

export const ENGINE_VERBS: readonly EngineVerb[] = ['complete', 'triage', 'reportability', 'whs', 'notification', 'handling'];

/** The engine verbs that take sibling rows, and so have a FIELDSET §4.0 writer code. `complete` adds none. */
export type RowVerb = Exclude<EngineVerb, 'complete'>;

export const ROW_VERBS: readonly RowVerb[] = ENGINE_VERBS.filter((v): v is RowVerb => v !== 'complete');

/** FIELDSET §4.0: the writer code of each dashboard verb (the §3.4.1 map's `writers` are these codes). */
export const WRITER_OF_VERB: Readonly<Record<RowVerb, SiblingWriter>> = {
  triage: 'TRI',
  reportability: 'REP',
  whs: 'WHS',
  notification: 'NOT',
  handling: 'HND',
};

/** The verb as a person reads it, at the start of each sibling `Note` ("Triage by …"). */
export const VERB_LABELS: Readonly<Record<EngineVerb, string>> = {
  complete: 'Report completed',
  triage: 'Triage',
  reportability: 'Reportability',
  whs: 'WHS',
  notification: 'Notification',
  handling: 'Handling',
};

/** The signed-in person (MSAL); `siteUserId` = the User Information List id, when it resolved (D-13-00-4). */
export type RecordActor = LodgeActor;

/**
 * One sibling row the person adds (FIELDSET §1.7, §3.4.1). `logKind` only on a `Note` row. `actorUpn` (the
 * investigator, default the actor) and `position` only on an `InvestigationStep` (§3.4.1 G-57). `at` is when it
 * happened, a Melbourne day and, when known, time; without it the row is dated `now`.
 */
export interface SiblingInput {
  entryType: string;
  logKind?: string;
  text: string;
  actorUpn?: string;
  position?: string;
  at?: WallTime;
}

/**
 * What the person saves. `set` is keyed by SharePoint internal name: text and choice columns take a string, day
 * columns a `YYYY-MM-DD` string or `{ day }`, instants a `{ day, time }` Melbourne wall time or a zoned ISO instant
 * (a computed deadline). `null` or a blank clears a column (which needs a `reason`). `auditId` is the `[a:<id>]` tag.
 */
export interface RecordVerbInput {
  set?: Readonly<Record<string, unknown>>;
  rows?: readonly SiblingInput[];
  reason?: string;
  /**
   * The reportability verb only (13-27, D-13-L-11): a hand-set `NotificationDueAt` in place of the computed one, with
   * its own reason (at least 10 characters), which goes to that column's `StateChange` Note.
   */
  dueOverride?: { at: WallTime | string; reason: string };
  auditId: string;
}

/** A verb build. `warnings` (e.g. `ambiguous-time`) never block it and come back either way. */
export type RecordVerbBuild =
  | ({ ok: true; warnings: Refusal[] } & VerbResult)
  | { ok: false; refusals: Refusal[]; warnings: Refusal[] };

// ---------------------------------------------------------------------------
// The rules the field set does not carry (D-13-20-*)
// ---------------------------------------------------------------------------

/** Where a stamp's value comes from: `now` → `toInstant(now)`; `actor` → the actor's UPN. */
export type StampSource = 'now' | 'actor';

/**
 * The columns a verb sets itself on every save that changes a column, never the user ("set when you save"; 13-22 /
 * 13-27 show this list). A user-supplied value is refused `stamped-column`.
 */
export const VERB_STAMPS: Readonly<
  Partial<Record<RecordRegister, Partial<Record<EngineVerb, Readonly<Record<string, StampSource>>>>>>
> = {
  incidents: { triage: { TriagedAt: 'now', TriagedByUPN: 'actor' } },
};

const STATUS = 'Status';

/** The one `Status` move this engine makes: a record's first triage takes it from `New` to `Actioning`. */
export const TRIAGE_STATUS: Readonly<Record<RecordRegister, Readonly<{ from: string; to: string }>>> =
  Object.fromEntries(
    RECORD_REGISTERS.map((r) => {
      const [from, to] = liveChoice(r, STATUS, 'New', 'Actioning');
      return [r, { from, to }];
    }),
  ) as Record<RecordRegister, { from: string; to: string }>;

/**
 * SharePoint defaults that mean "not decided yet" and so count as blank for required-ness (FIELDSET §1.1:
 * "`Pending` and null on `ReportabilityDecision` both mean undetermined"; §1.3: `Severity` = `No rating` fills at
 * create and triage sets the real value).
 */
export const UNDETERMINED: Readonly<Partial<Record<RecordRegister, Readonly<Record<string, readonly string[]>>>>> = {
  incidents: {
    Severity: liveChoice('incidents', 'Severity', 'No rating'),
    ReportabilityDecision: liveChoice('incidents', 'ReportabilityDecision', 'Pending'),
  },
};

/** Columns a later verb keeps (FIELDSET §2.1 `AwareAt`): changing an answered value needs a reason. */
export const KEPT_COLUMNS: Readonly<Partial<Record<RecordRegister, readonly string[]>>> = {
  incidents: ['AwareAt'],
};

/**
 * Columns a verb computes itself (13-27, D-13-27-3): `NotificationDueAt` on the reportability verb, from `AwareAt`,
 * the decision, the NDIS class and the WHS verb's `WorkSafeNotifiable` (`lib/notificationDeadlines`). A user value is
 * refused `computed-column`; a different deadline goes through `dueOverride`, with a reason. 13-28 (D-13-28-1): the
 * WHS verb computes the same column, from the same function, whenever its own inputs to it (`AwareAt`,
 * `WorkSafeNotifiable`, `WHS_DUE_INPUTS`) change, so an override made at reportability survives a WHS save of a claim
 * date.
 */
export const COMPUTED_COLUMNS: Readonly<Partial<Record<RecordRegister, Partial<Record<EngineVerb, readonly string[]>>>>> = {
  incidents: { reportability: ['NotificationDueAt'], whs: ['NotificationDueAt'] },
};

/** 13-28 (D-13-28-1): the WHS verb's columns that feed `NotificationDueAt`; it is recomputed only when one changes. */
export const WHS_DUE_INPUTS: readonly string[] = ['AwareAt', 'WorkSafeNotifiable'];

/**
 * 13-28 (D-13-28-2; refines D-13-17-6's transcription, as D-13-20-5 did for the notices): the WHS verb's "done"
 * columns, which record that a timed duty was done. The field set requires each "with" its anchor (`ClaimForwardedOn`
 * with `ClaimReceivedOn` …), which would stop HR & WHS recording a claim's receipt until it had been forwarded, and
 * would leave the "Forward by" day nothing to count down. So a blank here means "not done yet", and the panel's due
 * day turns amber once it passes (D-13-15-4). The classification asked "on receipt" stays required with its anchor
 * (`ClaimType`, `MentalInjuryClaim`, `InjuryNoticeMethod`: OB-OHS-30, -36, -25).
 */
export const WHS_NOT_YET_DONE: readonly string[] = [
  'InjuryNoticeAcknowledgedOn',
  'ClaimAcknowledgedOn',
  'ClaimForwardedOn',
  'MedicalCertificateForwardedOn',
  'MentalInjuryNotifiedOn',
];

/**
 * Instants that record something that has already happened, so they can't be in the future (13-27, D-13-27-10):
 * when Harmony became aware (a later `AwareAt` would make every deadline later), and each notice or report given.
 */
export const NOT_IN_FUTURE: Readonly<Partial<Record<RecordRegister, readonly string[]>>> = {
  incidents: [
    'AwareAt',
    'NdisInitialNoticeAt',
    'NdisFurtherNoticeAt',
    'SirsNoticeAt',
    'SirsAdditionalNoticeAt',
    'FinalReportGivenAt',
    'WorkSafeNotifiedAt',
    'WorkSafeWrittenRecordAt',
  ],
};

/**
 * The notification verb's per-notice rules (13-27, D-13-27-5; refines D-13-20-5 / D-13-17-6): when a notice is
 * recorded, what the instrument says the notice itself carries is recorded with it. Every other notification column
 * stays "not given yet" until its own visit.
 * - NDIS initial notice: the method (OB-NDIS-49, IMRI s 20(5)) and the notifier (OB-NDIS-55, s 20(2)(e)).
 * - NDIS 5-business-day notice: the further actions proposed (OB-NDIS-61, s 20(4)(b) / s 21(3)(g)) and the notifier.
 * - SIRS notice: the method (OB-AC-68), the notifier and their position (OB-AC-76: the position is SIRS-only), and the
 *   further actions proposed (OB-AC-75: in the notice itself).
 * - WorkSafe notice (13-28, D-13-28-5): who notified (OB-OHS-07, "with OB-OHS-06"). The Authority's reference
 *   (OB-OHS-08) is not required: the row says the text does not require it, and it may come later.
 */
export const NOTICE_COMPANIONS: Readonly<Partial<Record<RecordRegister, Readonly<Record<string, readonly string[]>>>>> = {
  incidents: {
    NdisInitialNoticeAt: ['NdisInitialNoticeMethod', 'NotifierUPN'],
    NdisFurtherNoticeAt: ['FurtherActionsProposed', 'NotifierUPN'],
    SirsNoticeAt: ['SirsNoticeMethod', 'NotifierUPN', 'NotifierPosition', 'FurtherActionsProposed'],
    WorkSafeNotifiedAt: ['WorkSafeNotifiedByUPN'],
  },
};

/** The shortest reason a reportability save takes (13-27; the closure verbs' 10, D-13-21-7). */
export const REPORTABILITY_REASON_MIN = 10;

/**
 * 13.2-03 (D-13.2-P-3): the provenance columns `complete` never writes, in any form, even blank, with why. Neither is
 * in its column list; they are named so a person is told why, not just "not this verb". The daily reconcile matches
 * register rows by `ExternalId` (`reconcile_intake_v1406.py`).
 */
export const PROVENANCE_LOCKED: Readonly<Partial<Record<RecordRegister, Readonly<Record<string, string>>>>> = {
  incidents: {
    ReportedByEmail: "Reported by can't be changed: it records who made the report.",
    ExternalId: "The ShiftCare ticket number can't be changed: it ties this record to the daily reconcile.",
  },
};

/**
 * 13.2-03 (D-13.2-P-2): columns `complete` may only clear, with the words a person needs. `InjuredWorkerUPN` has one
 * writer, FieldLink's "I am the injured worker"; the dashboard names the worker in `InjuredWorkerName` instead.
 */
export const CLEAR_ONLY: Readonly<Partial<Record<RecordRegister, Readonly<Record<string, string>>>>> = {
  incidents: {
    InjuredWorkerUPN: "can only be cleared here. Name the injured worker in \"Injured worker's name\" instead.",
  },
};

/**
 * 13.2-03 (D-13.2-P-3): a lodge column `complete` never changes on a row that came from the ShiftCare intake (a row
 * with an `ExternalId`): the reconcile filters register rows by it.
 */
const INTAKE_LOCKED: Readonly<Partial<Record<RecordRegister, { key: string; column: string }>>> = {
  incidents: { key: 'ExternalId', column: 'ReportedOn' },
};

/** 13.2-03: the statuses in which a report can no longer be completed (`record-closed`). */
const CLOSED_STATUSES: Readonly<Partial<Record<RecordRegister, readonly string[]>>> = {
  incidents: liveChoice('incidents', 'Status', 'Completed', 'Cancelled'),
};

const OCCURRED_ON = 'OccurredOn';
const REPORTED_ON = 'ReportedOn';

const DECISION = 'ReportabilityDecision';
const AWARE_AT = 'AwareAt';
const DUE_AT = 'NotificationDueAt';
const WORKSAFE_NOTIFIABLE = 'WorkSafeNotifiable';
const [WORKSAFE_YES] = liveChoice('incidents', WORKSAFE_NOTIFIABLE, 'Yes');

// Load-time checks: every stamp and kept column is a column of a verb that writes it.
for (const [register, byVerb] of Object.entries(VERB_STAMPS) as [RecordRegister, Partial<Record<EngineVerb, Record<string, StampSource>>>][]) {
  for (const [verb, stamps] of Object.entries(byVerb) as [EngineVerb, Record<string, StampSource>][]) {
    for (const column of Object.keys(stamps)) {
      if (!verbColumns(register, verb).includes(column)) {
        throw new Error(`recordVerbs: stamp ${register}.${column} is not a ${verb} column`);
      }
    }
  }
}
for (const [register, columns] of Object.entries(KEPT_COLUMNS) as [RecordRegister, readonly string[]][]) {
  for (const column of columns) {
    if (!ENGINE_VERBS.some((v) => verbColumns(register, v).includes(column))) {
      throw new Error(`recordVerbs: kept column ${register}.${column} has no verb`);
    }
  }
}
for (const [register, byColumn] of Object.entries(UNDETERMINED) as [RecordRegister, Record<string, readonly string[]>][]) {
  for (const column of Object.keys(byColumn)) choicesOf(register, column); // throws unless a live choice column
}
for (const [register, byVerb] of Object.entries(COMPUTED_COLUMNS) as [RecordRegister, Partial<Record<EngineVerb, readonly string[]>>][]) {
  for (const [verb, cols] of Object.entries(byVerb) as [EngineVerb, readonly string[]][]) {
    for (const column of cols) {
      if (!verbColumns(register, verb).includes(column)) throw new Error(`recordVerbs: computed ${register}.${column} is not a ${verb} column`);
    }
  }
}
for (const [register, cols] of Object.entries(NOT_IN_FUTURE) as [RecordRegister, readonly string[]][]) {
  for (const column of cols) {
    if (fieldOf(register, column)?.kind !== 'instant') throw new Error(`recordVerbs: ${register}.${column} is not an instant column`);
  }
}
for (const column of [...WHS_DUE_INPUTS, ...WHS_NOT_YET_DONE]) {
  if (!verbColumns('incidents', 'whs').includes(column)) throw new Error(`recordVerbs: incidents.${column} is not a whs column`);
}
for (const [register, byNotice] of Object.entries(NOTICE_COMPANIONS) as [RecordRegister, Record<string, readonly string[]>][]) {
  for (const column of [...Object.keys(byNotice), ...Object.values(byNotice).flat()]) {
    if (!verbColumns(register, 'notification').includes(column)) throw new Error(`recordVerbs: ${register}.${column} is not a notification column`);
  }
}
// 13.2-03: complete's own lists name columns it has (clear-only, intake-locked) or never has (provenance).
for (const [register, byColumn] of Object.entries(CLEAR_ONLY) as [RecordRegister, Record<string, string>][]) {
  for (const column of Object.keys(byColumn)) {
    if (!verbColumns(register, 'complete').includes(column)) throw new Error(`recordVerbs: clear-only ${register}.${column} is not a complete column`);
  }
}
for (const [register, lock] of Object.entries(INTAKE_LOCKED) as [RecordRegister, { key: string; column: string }][]) {
  if (!verbColumns(register, 'complete').includes(lock.column)) throw new Error(`recordVerbs: intake-locked ${register}.${lock.column} is not a complete column`);
}
for (const [register, byColumn] of Object.entries(PROVENANCE_LOCKED) as [RecordRegister, Record<string, string>][]) {
  for (const column of Object.keys(byColumn)) {
    if (verbColumns(register, 'complete').includes(column)) throw new Error(`recordVerbs: provenance ${register}.${column} is a complete column`);
  }
}

// ---------------------------------------------------------------------------
// Blanks and comparison
// ---------------------------------------------------------------------------

/** Blank: `null`, `undefined` or an all-space string (exported for 13-21). */
export function isBlank(value: unknown): boolean {
  return value === null || value === undefined || (typeof value === 'string' && value.trim() === '');
}

/**
 * A required column's refusal in its own plain words, where the generic "X is required." would not say why.
 * D-13-17-6, ruled 13 Oct 2026 (D-13-33): CI triage must name an owner (FIELDSET's "a blank owner stays blank" is
 * read as "not yet named", never as an answer at triage).
 */
const REQUIRED_WORDS: Readonly<Partial<Record<RecordRegister, Partial<Record<EngineVerb, Readonly<Record<string, string>>>>>>> = {
  ci: { triage: { OwnerUPN: 'Name the owner: every improvement leaves triage with someone responsible for it.' } },
};

/** Blank for required-ness: blank, or an undetermined default (`UNDETERMINED`). */
export function isUnanswered(register: RecordRegister, column: string, value: unknown): boolean {
  if (isBlank(value)) return true;
  return typeof value === 'string' && (UNDETERMINED[register]?.[column] ?? []).includes(value);
}

/** A stored value as an audit `OldValue` (the string as stored, `''` when blank). */
function stored(value: unknown): string {
  return isBlank(value) ? '' : String(value);
}

/**
 * Old and new are the same once normalised by kind (§1.7): days by their Melbourne day (00:00Z, Pacific midnight
 * and a plain day all compare equal), instants exactly, text trimmed, UPNs case-insensitively, choices exactly.
 */
function sameValue(spec: FieldSpec | undefined, old: unknown, next: RecordFieldValue): boolean {
  const oldBlank = isBlank(old);
  const nextBlank = isBlank(next);
  if (oldBlank || nextBlank) return oldBlank && nextBlank;
  const a = String(old);
  const b = String(next);
  switch (spec?.kind) {
    case 'day-new':
    case 'day-existing': {
      // 13.2-03: a day column that takes a time when known (OccurredOn, ReportedOn) compares exactly once either side
      // carries a time, so adding or correcting the time on the same day is a change, not "nothing changed".
      if (spec.timeWhenKnown && (classifyInstant(a) === 'time-of-day' || classifyInstant(b) === 'time-of-day')) {
        const ta = Date.parse(a);
        const tb = Date.parse(b);
        return Number.isNaN(ta) || Number.isNaN(tb) ? a === b : ta === tb;
      }
      const da = melbourneDayOf(a);
      const db = melbourneDayOf(b);
      return da !== null && db !== null ? da === db : a === b;
    }
    case 'instant': {
      const ta = Date.parse(a);
      const tb = Date.parse(b);
      return Number.isNaN(ta) || Number.isNaN(tb) ? a === b : ta === tb;
    }
    case 'text':
    case 'textarea':
    case 'position':
      return a.trim() === b.trim();
    case 'upn':
      return a.trim().toLowerCase() === b.trim().toLowerCase();
    default:
      return a === b;
  }
}

// ---------------------------------------------------------------------------
// The diff and the drafts (exported for 13-21)
// ---------------------------------------------------------------------------

/** One changed column: the old value as stored and the new one as sent (`''` when blank / cleared). */
export interface ColumnChange {
  column: string;
  oldValue: string;
  newValue: string;
  cleared: boolean;
}

/**
 * The changed subset of `proposed` against `row` (normalised by kind, see `sameValue`), in `proposed`'s key order.
 * `null` in `proposed` clears. Unchanged columns are in neither `patch` nor `changes`, so the two are one-to-one.
 */
export function diffRecord(
  row: RecordRow,
  proposed: Readonly<Record<string, RecordFieldValue>>,
): { patch: RecordPatch; changes: ColumnChange[] } {
  const patch: RecordPatch = {};
  const changes: ColumnChange[] = [];
  for (const [column, next] of Object.entries(proposed)) {
    const spec = fieldOf(row.register, column);
    if (!spec) throw new Error(`recordVerbs: ${row.register}.${column} has no field spec`);
    const old = row.fields[column];
    if (sameValue(spec, old, next)) continue;
    const cleared = isBlank(next);
    patch[column] = cleared ? null : next;
    changes.push({ column, oldValue: stored(old), newValue: cleared ? '' : String(next), cleared });
  }
  return { patch, changes };
}

/** A sibling draft before its `[a:<id>]` tag is known: the Note is `<noteHead> <tag>[ <noteTail>]`. */
export type PendingDraft = Omit<SiblingDraft, 'Note' | 'auditId'> & { noteHead: string; noteTail?: string };

/** Who and when, for the drafts of one save. */
export interface DraftContext {
  /** The verb key, used in `Title` (`triage INC-12: Severity`). */
  verb: string;
  /** The verb as a person reads it, used in `Note` (`Triage by …`). */
  label: string;
  actorUpn: string;
  actorLookupId?: string;
  now: Date;
  /** Appended to every `StateChange` Note as `Reason: …`. */
  reason?: string;
  /** Appended to one column's `StateChange` Note, after the reason (13-27: the `NotificationDueAt` override text). */
  columnNotes?: Readonly<Record<string, string>>;
}

/**
 * One `StateChange` draft per change (§1.7): `FieldChanged`, `OldValue`, `NewValue`, no `LogKind`. Only the row's ref
 * is read, so 13-32's Internal Audits verbs draft through it too.
 */
export function draftStateChanges(row: Pick<RecordRow, 'ref'>, changes: readonly ColumnChange[], ctx: DraftContext): PendingDraft[] {
  return changes.map((ch) => ({
    Title: `${ctx.verb} ${row.ref}: ${ch.column}`,
    ParentRef: row.ref,
    EntryType: 'StateChange',
    FieldChanged: ch.column,
    OldValue: ch.oldValue,
    NewValue: ch.newValue,
    EntryTimestamp: toInstant(ctx.now),
    ActorUPN: ctx.actorUpn,
    ...(ctx.actorLookupId ? { ActorLookupId: ctx.actorLookupId } : {}),
    noteHead: `${ctx.label} by ${ctx.actorUpn}.`,
    ...noteTailOf(ctx, ch.column),
  }));
}

/** `Reason: …`, then the column's own note (13-27), when there is either. */
function noteTailOf(ctx: DraftContext, column: string): { noteTail?: string } {
  const tail = [ctx.reason ? `Reason: ${ctx.reason}` : '', ctx.columnNotes?.[column] ?? ''].filter(Boolean).join(' ');
  return tail ? { noteTail: tail } : {};
}

/**
 * Tag every draft of one save (D-13-07-3, D-13-L-5): a single draft carries `[a:<auditId>]`; several carry
 * `[a:<auditId>.<n>]`, n = 1… in order, so a retry's de-dupe can tell rows with no `FieldChanged` apart. Each
 * draft's `auditId` is its own tag id.
 */
export function finaliseDrafts(pending: readonly PendingDraft[], auditId: string): SiblingDraft[] {
  return pending.map(({ noteHead, noteTail, ...draft }, i) => {
    const id = pending.length === 1 ? auditId : `${auditId}.${i + 1}`;
    return { ...draft, Note: `${noteHead} ${actionTag(id)}${noteTail ? ` ${noteTail}` : ''}`, auditId: id };
  });
}

// ---------------------------------------------------------------------------
// Values by kind
// ---------------------------------------------------------------------------

/** A `set` value after `cleanColumn` (exported for 13-21). */
export type Cleaned =
  | { kind: 'blank' }
  | { kind: 'value'; value: string; warning?: Refusal }
  | { kind: 'refused'; code: RecordRefusalCode; message: string };

const blank: Cleaned = { kind: 'blank' };
const refusedValue = (code: RecordRefusalCode, message: string): Cleaned => ({ kind: 'refused', code, message });

function isWallTime(value: unknown): value is WallTime {
  return typeof value === 'object' && value !== null && !Array.isArray(value) && 'day' in value;
}

/** A Melbourne wall time → an instant, or a refusal in the person's words; the repeated hour warns. */
function wallInstant(label: string, column: string, day: string, time: string): Cleaned {
  const r = melbourneWallToInstant(day, time);
  if (!r.ok) {
    const message =
      r.code === 'nonexistent-time'
        ? `${label}: that time didn't exist that night (clocks went forward). Check the time.`
        : r.code === 'invalid-time'
          ? `${label}: enter the time as HH:MM (24-hour).`
          : `${label}: enter a real date as YYYY-MM-DD.`;
    return refusedValue(r.code, message);
  }
  if (!r.ambiguous) return { kind: 'value', value: r.value };
  return {
    kind: 'value',
    value: r.value,
    warning: {
      code: 'ambiguous-time',
      column,
      message: `${label}: ${time} happened twice that night (clocks went back); we've recorded the first, AEDT.`,
    },
  };
}

/** An instant column: `{ day, time }` (Melbourne) or a zoned ISO instant; a day alone or a zoneless time is refused. */
function cleanInstant(spec: FieldSpec, raw: unknown): Cleaned {
  if (typeof raw === 'string') {
    if (raw.trim() === '') return blank;
    if (raw.includes('T') && melbourneDayOf(raw) !== null) return { kind: 'value', value: toInstant(new Date(Date.parse(raw))) };
    return refusedValue('invalid-date', `${spec.label}: enter a date and a time.`);
  }
  if (!isWallTime(raw)) return refusedValue('invalid-date', `${spec.label}: enter a date and a time.`);
  if (isBlank(raw.day) && isBlank(raw.time)) return blank;
  if (typeof raw.day !== 'string' || typeof raw.time !== 'string' || raw.time.trim() === '') {
    return refusedValue('invalid-date', `${spec.label}: enter a date and a time.`);
  }
  return wallInstant(spec.label, spec.name, raw.day, raw.time);
}

/** A day column: `YYYY-MM-DD` or `{ day }`, as §1.4's payload for its kind; a time is refused. */
function cleanDay(spec: FieldSpec, raw: unknown): Cleaned {
  let day: unknown = raw;
  if (isWallTime(raw)) {
    if (!isBlank(raw.time)) return refusedValue('invalid-date', `${spec.label} takes a date, not a time.`);
    day = raw.day;
  } else if (typeof raw !== 'string') {
    return refusedValue('invalid-date', `${spec.label}: enter a date.`);
  }
  if (isBlank(day)) return blank;
  const r = spec.kind === 'day-new' ? dayNew(day as string) : dayExisting(day as string);
  return r.ok ? { kind: 'value', value: r.value } : refusedValue(r.code, `${spec.label}: enter a real date as YYYY-MM-DD.`);
}

/** One `set` value, cleaned and checked by its column's kind. */
export function cleanColumn(register: RecordRegister, spec: FieldSpec, raw: unknown): Cleaned {
  switch (spec.kind) {
    case 'text':
    case 'textarea':
    case 'position': {
      if (raw === null || raw === undefined) return blank;
      if (spec.cap === undefined) throw new Error(`recordVerbs: ${register}.${spec.name} has no cap`);
      const r = cleanText(raw as string, spec.cap);
      if (!r.ok) {
        return r.code === 'too-long'
          ? refusedValue('too-long', `${spec.label} is ${r.length} characters; the limit is ${r.cap}.`)
          : refusedValue('not-text', `${spec.label} must be text.`);
      }
      if (r.value === '') return blank;
      if (spec.kind === 'position' && r.value.includes('@')) {
        return refusedValue('position-not-person', `${spec.label} holds a role or job title, never a person's address.`);
      }
      return { kind: 'value', value: r.value };
    }
    case 'choice': {
      if (isBlank(raw)) return blank;
      const r = checkChoice(raw as string, choicesOf(register, spec.name));
      return r.ok ? { kind: 'value', value: r.value } : refusedValue(r.code, `${spec.label}: choose one of the listed options.`);
    }
    case 'upn': {
      if (isBlank(raw)) return blank;
      const r = checkUpn(raw as string);
      if (r.ok) return { kind: 'value', value: r.value };
      return r.code === 'guest-upn'
        ? refusedValue(r.code, `${spec.label}: a guest account can't be recorded here; use their Harmony account.`)
        : refusedValue(r.code, `${spec.label}: enter one email address, not a name.`);
    }
    case 'instant':
      if (raw === null || raw === undefined) return blank;
      return cleanInstant(spec, raw);
    case 'day-new':
    case 'day-existing':
      if (raw === null || raw === undefined) return blank;
      return cleanDay(spec, raw);
    case 'attachments':
      if (raw === null || raw === undefined || (Array.isArray(raw) && raw.length === 0)) return blank;
      return refusedValue('not-writable', `${spec.label}: add attachments on the item's SharePoint form.`);
    default:
      // A verb column of a kind no verb writes (a config change): fail loudly, never write blind.
      throw new Error(`recordVerbs: ${register}.${spec.name} has kind ${spec.kind}, which no verb writes`);
  }
}

/** Free text a person writes into a sibling Note (row text, a reason): cleaned, capped, no contact details (§1.6). */
export function cleanNoteText(raw: unknown, label: string): { ok: true; value: string } | { ok: false; code: RecordRefusalCode; message: string } {
  const r = cleanText(raw as string, TEXT_CAPS.textarea, { multiline: true });
  if (!r.ok) {
    return r.code === 'too-long'
      ? { ok: false, code: 'too-long', message: `${label} is ${r.length} characters; the limit is ${r.cap}.` }
      : { ok: false, code: 'not-text', message: `${label} must be text.` };
  }
  if (looksLikeContact(r.value)) {
    return { ok: false, code: 'contact-in-note', message: `${label} looks like it contains a phone number or email. Contact details go in their own fields.` };
  }
  return { ok: true, value: r.value };
}

// ---------------------------------------------------------------------------
// Required (FIELDSET §1.1)
// ---------------------------------------------------------------------------

/**
 * Whether a column's rule applies to this verb (D-13-20): the verb's stage rule, except that the notification verb,
 * which records one notice per visit, applies only a conditional whose `when` is one of its own columns.
 */
function ruleFor(verb: EngineVerb, spec: FieldSpec, own: ReadonlySet<string>): RequiredRule | undefined {
  const rule = spec.required[STAGE_OF_VERB[verb]];
  if (verb === 'reportability' && spec.register === 'incidents') {
    // 13-27 (D-13-27-3, resolving D-13-20-8): a clock starts only for a decision that has one, so `AwareAt` is
    // required for a reportable decision only; `NotificationDueAt` is computed, never a person's blank.
    if (spec.name === AWARE_AT) return { when: DECISION, in: REPORTABLE_DECISIONS };
    if (spec.name === DUE_AT) return undefined;
  }
  if (verb === 'whs' && spec.register === 'incidents') {
    // 13-28 (D-13-28-1, closing D-13-20-8 for WHS): the WorkSafe clock starts only for a notifiable incident, so the
    // WHS verb needs `AwareAt` only then; `NotificationDueAt` is computed. A done column's blank is "not yet" (D-13-28-2).
    if (spec.name === AWARE_AT) return { when: WORKSAFE_NOTIFIABLE, in: [WORKSAFE_YES] };
    if (spec.name === DUE_AT || WHS_NOT_YET_DONE.includes(spec.name)) return undefined;
  }
  if (verb !== 'notification') return rule;
  return typeof rule === 'object' && own.has(rule.when) ? rule : undefined;
}

/** Does a rule's condition hold on the merged record? `false` when the column it reads was refused. */
export function holds(register: RecordRegister, rule: RequiredRule | undefined, merged: (c: string) => unknown, bad: ReadonlySet<string>): boolean {
  if (rule === 'always') return true;
  if (rule === undefined || rule === 'optional') return false;
  if (bad.has(rule.when)) return false;
  const raw = merged(rule.when);
  const v = isUnanswered(register, rule.when, raw) ? '' : String(raw);
  return 'in' in rule ? rule.in.includes(v) : !rule.notIn.includes(v);
}

// ---------------------------------------------------------------------------
// Rows (§1.7, §3.4.1)
// ---------------------------------------------------------------------------

const NOTE: AuditEntryType = 'Note';
const INVESTIGATION_STEP: AuditEntryType = 'InvestigationStep';
const STATE_CHANGE: AuditEntryType = 'StateChange';

type RowBuild = { ok: true; draft: PendingDraft; warning?: Refusal } | { ok: false; code: RecordRefusalCode; message: string };

function buildRow(
  verb: RowVerb,
  row: RecordRow,
  input: SiblingInput,
  column: string,
  actorUpn: string,
  actor: RecordActor,
  now: Date,
): RowBuild {
  const register = row.register;
  const writer = WRITER_OF_VERB[verb];
  const entryType = input?.entryType;
  if (typeof entryType !== 'string' || !ENTRY_TYPES.includes(entryType) || entryType === STATE_CHANGE) {
    return { ok: false, code: 'entrytype-not-allowed', message: `A ${String(entryType)} row can't be added here.` };
  }

  // --- EntryType and LogKind against the §3.4.1 map ----------------------------------------------------------
  let logKind: string | undefined;
  if (entryType === NOTE) {
    if (isBlank(input.logKind)) return { ok: false, code: 'logkind-required', message: 'Choose what kind of entry this is.' };
    const kind = input.logKind as string;
    const use = LOG_KIND_MAP[kind];
    if (!LOG_KINDS.includes(kind) || !use || !use.registers.includes(register) || !use.writers.includes(writer)) {
      return { ok: false, code: 'logkind-not-allowed', message: `A ${kind} entry isn't recorded by ${VERB_LABELS[verb].toLowerCase()} on this register.` };
    }
    logKind = kind;
  } else {
    if (!isBlank(input.logKind)) {
      return { ok: false, code: 'logkind-not-allowed', message: `A ${entryType} row carries no entry kind.` };
    }
    const use = ENTRY_TYPE_MAP[entryType];
    if (!use || !use.registers.includes(register) || !use.writers.includes(writer)) {
      return { ok: false, code: 'entrytype-not-allowed', message: `A ${entryType} row isn't recorded by ${VERB_LABELS[verb].toLowerCase()} on this register.` };
    }
  }

  // --- Text ---------------------------------------------------------------------------------------------------
  const text = cleanNoteText(input.text ?? null, 'The entry');
  if (!text.ok) return text;
  if (text.value === '') return { ok: false, code: 'required', message: 'Write what happened.' };

  // --- The investigator and position (InvestigationStep only, §3.4.1 G-57) ------------------------------------
  const step = entryType === INVESTIGATION_STEP;
  if (!step && (input.actorUpn !== undefined || input.position !== undefined)) {
    return { ok: false, code: 'not-applicable', message: 'An investigator and position belong to an investigation step only.' };
  }
  let rowActor = actorUpn;
  let position = '';
  if (step) {
    if (!isBlank(input.actorUpn)) {
      const u = checkUpn(input.actorUpn as string);
      if (!u.ok) {
        return { ok: false, code: u.code, message: u.code === 'guest-upn' ? "The investigator: a guest account can't be recorded here." : 'The investigator: enter one email address, not a name.' };
      }
      rowActor = u.value;
    }
    if (!isBlank(input.position)) {
      const p = cleanText(input.position as string, TEXT_CAPS.text);
      if (!p.ok) return { ok: false, code: p.code === 'too-long' ? 'too-long' : 'not-text', message: "The investigator's position is too long or not text." };
      if (p.value.includes('@')) {
        return { ok: false, code: 'position-not-person', message: "The investigator's position is a role or job title, never an address." };
      }
      position = p.value;
    }
  }

  // --- When it happened (ISS-202: first_action_on reads this) --------------------------------------------------
  let at = toInstant(now);
  let warning: Refusal | undefined;
  if (input.at !== undefined) {
    if (!isWallTime(input.at) || typeof input.at.day !== 'string') {
      return { ok: false, code: 'invalid-date', message: 'When it happened: enter a date.' };
    }
    let when: Cleaned;
    if (isBlank(input.at.time)) {
      const d = dayExisting(input.at.day);
      when = d.ok ? { kind: 'value', value: d.value } : refusedValue(d.code, 'When it happened: enter a real date as YYYY-MM-DD.');
    } else {
      when = wallInstant('When it happened', column, input.at.day, String(input.at.time));
    }
    if (when.kind === 'refused') return { ok: false, code: when.code, message: when.message };
    if (when.kind === 'value') {
      const future = notInFuture(when.value, now);
      if (!future.ok) return { ok: false, code: 'in-future', message: "When it happened can't be in the future." };
      at = when.value;
      warning = when.warning;
    }
  }

  // --- The draft --------------------------------------------------------------------------------------------
  const noteHead = step
    ? `${text.value}\nInvestigator: ${rowActor}${position ? `, ${position}` : ''}. Recorded by ${actorUpn}.`
    : `${text.value}\n${VERB_LABELS[verb]} by ${actorUpn}.`;
  const draft: PendingDraft = {
    Title: `${verb} ${row.ref}: ${logKind ?? entryType}`,
    ParentRef: row.ref,
    EntryType: entryType as AuditEntryType,
    ...(logKind ? { LogKind: logKind } : {}),
    EntryTimestamp: at,
    ActorUPN: rowActor,
    // The lookup id is the actor's; it would contradict an investigator who is someone else.
    ...(actor.siteUserId && rowActor === actorUpn ? { ActorLookupId: actor.siteUserId } : {}),
    noteHead,
  };
  return { ok: true, draft, ...(warning ? { warning } : {}) };
}

// ---------------------------------------------------------------------------
// The builder
// ---------------------------------------------------------------------------

/**
 * Build one register verb save: the PATCH and its sibling drafts, or every refusal in one pass. See the module
 * comment for the rules and their sources.
 */
export function buildRecordVerb(
  verb: EngineVerb,
  row: RecordRow,
  actor: RecordActor,
  input: RecordVerbInput,
  now: Date,
): RecordVerbBuild {
  if (!(ENGINE_VERBS as readonly string[]).includes(verb)) {
    throw new RecordVerbError('unknown-verb', `recordVerbs: no verb '${String(verb)}' here (closure verbs are 13-21's)`);
  }
  const register = row.register;
  if (!verbsOf(register).includes(verb)) {
    throw new RecordVerbError('verb-not-on-register', `recordVerbs: ${register} has no ${verb} verb`);
  }
  if (!input || typeof input.auditId !== 'string' || input.auditId.trim() === '') {
    throw new RecordVerbError('audit-id-required', 'buildRecordVerb: input.auditId is required (the [a:<id>] tag)');
  }
  if (verb === 'complete') return buildComplete(row, actor, input, now);
  const rowVerb: RowVerb = verb;

  const refusals: Refusal[] = [];
  const warnings: Refusal[] = [];
  const refuse = (code: RecordRefusalCode, column: string | undefined, message: string) =>
    refusals.push(column === undefined ? { code, message } : { code, column, message });

  // --- The actor (D-13-L-8) ------------------------------------------------------------------------------------
  const actorCheck = checkUpn(actor?.upn as string);
  if (!actorCheck.ok) refuse('invalid-actor', undefined, 'The signed-in account has no usable email address.');
  const actorUpn = actorCheck.ok ? actorCheck.value : '';

  // --- The reason ----------------------------------------------------------------------------------------------
  let reason = '';
  let reasonRefused = false;
  if (input.reason !== undefined && input.reason !== null) {
    const r = cleanNoteText(input.reason, 'The reason');
    if (r.ok) reason = r.value;
    else {
      refuse(r.code, 'reason', r.message);
      reasonRefused = true;
    }
  }

  const columns = verbColumns(register, verb);
  const own = new Set(columns);
  const stamps = VERB_STAMPS[register]?.[verb] ?? {};
  const kept = new Set(KEPT_COLUMNS[register] ?? []);
  const computedCols = new Set(COMPUTED_COLUMNS[register]?.[verb] ?? []);
  const notFuture = new Set(NOT_IN_FUTURE[register] ?? []);
  const deciding = verb === 'reportability' && register === 'incidents';
  if (input.dueOverride !== undefined && !computedCols.has(DUE_AT)) {
    refuse('not-applicable', 'dueOverride', 'A notification deadline is overridden only with the reportability decision or the WHS answer.');
  }

  // --- Pass 1: every key in set ----------------------------------------------------------------------------------
  const given = new Map<string, RecordFieldValue>();
  const bad = new Set<string>();
  for (const [key, raw] of Object.entries(input.set ?? {})) {
    if (raw === undefined) continue;
    if (key === STATUS) {
      refuse('status-via-closure', key, 'Status moves only through triage (New to Actioning) and the closure verbs.');
      continue;
    }
    if (!own.has(key)) {
      refuse('not-this-verb', key, `${key} isn't recorded by ${VERB_LABELS[verb].toLowerCase()}.`);
      continue;
    }
    if (key in stamps) {
      refuse('stamped-column', key, `${key} is set when you save; it can't be entered.`);
      continue;
    }
    const spec = fieldOf(register, key);
    if (!spec) throw new Error(`recordVerbs: ${register} ${verb} column ${key} has no field spec`);
    if (computedCols.has(key)) {
      refuse('computed-column', key, `${spec.label} is worked out from the record when you save. To set another, override it with a reason.`);
      continue;
    }
    const r = cleanColumn(register, spec, raw);
    if (r.kind === 'refused') {
      refuse(r.code, key, r.message);
      bad.add(key);
      continue;
    }
    if (r.kind === 'value' && notFuture.has(key) && !notInFuture(r.value, now).ok) {
      refuse('in-future', key, `${spec.label} can't be in the future.`);
      bad.add(key);
      continue;
    }
    if (r.kind === 'value' && r.warning) warnings.push(r.warning);
    given.set(key, r.kind === 'value' ? r.value : null);
  }

  // --- Pass 2: what changes, and the clear / kept rules ------------------------------------------------------------
  const userProposed: Record<string, RecordFieldValue> = {};
  for (const column of columns) if (given.has(column)) userProposed[column] = given.get(column) as RecordFieldValue;
  const userDiff = diffRecord(row, userProposed);
  // 13-27 (D-13-27-4): every reportability save that changes something carries a reason of at least 10 characters,
  // which goes on every StateChange row (the 13-10 decide's rule). It also covers a clear or a kept-column change,
  // so a missing reason is said once.
  if (deciding && userDiff.changes.length > 0 && !reasonRefused && reason.length < REPORTABILITY_REASON_MIN) {
    refuse('reason-required', 'reason', `Write your reason (at least ${REPORTABILITY_REASON_MIN} characters). It goes on the trail with the decision.`);
    reasonRefused = true;
  }
  const accepted: Record<string, RecordFieldValue> = {};
  for (const ch of userDiff.changes) {
    const label = fieldOf(register, ch.column)?.label ?? ch.column;
    if (ch.cleared && !reason) {
      if (!reasonRefused) refuse('clear-needs-reason', ch.column, `Clearing ${label} needs a reason: a blank is never an answer.`);
      bad.add(ch.column);
      continue;
    }
    if (kept.has(ch.column) && ch.oldValue !== '' && !ch.cleared && !reason) {
      if (!reasonRefused) refuse('change-needs-reason', ch.column, `${label} was already recorded; changing it needs a reason.`);
      bad.add(ch.column);
      continue;
    }
    accepted[ch.column] = userDiff.patch[ch.column];
  }

  // --- Pass 3: stamps and Status, only on a save that changes a column ------------------------------------------
  const proposed: Record<string, RecordFieldValue> = {};
  const changesColumns = Object.keys(accepted).length > 0;
  const computed: Record<string, RecordFieldValue> = {};
  const columnNotes: Record<string, string> = {};
  // 13-28 (D-13-28-1): the WHS verb recomputes only when an input to the deadline changes, or with an override, which
  // it may also save on its own (the reportability verb restates its decision on every save, so it never needs to).
  const whsOverride = verb === 'whs' && input.dueOverride !== undefined;
  const recompute =
    computedCols.has(DUE_AT) &&
    (changesColumns || whsOverride) &&
    (verb !== 'whs' || whsOverride || WHS_DUE_INPUTS.some((c) => c in accepted));
  if (recompute) {
    // 13-27 (D-13-L-11, D-13-27-3): NotificationDueAt from the record as it will be after this save.
    const after = (c: string): unknown => (c in accepted ? accepted[c] : row.fields[c]);
    const due = notificationDue({
      awareAt: after(AWARE_AT) as string | null,
      decision: after(DECISION) as string | null,
      ndisNoticeClass: after('NdisNoticeClass') as string | null,
      sirsKind: after('SirsKind') as string | null,
      workSafeNotifiable: after(WORKSAFE_NOTIFIABLE) as string | null,
    });
    if (input.dueOverride !== undefined) {
      const spec = fieldOf(register, DUE_AT) as FieldSpec;
      const at = cleanColumn(register, spec, input.dueOverride.at);
      const why = cleanNoteText(input.dueOverride.reason ?? null, 'The override reason');
      if (at.kind !== 'value') {
        refuse(at.kind === 'refused' ? at.code : 'invalid-date', 'dueOverride', at.kind === 'refused' ? at.message : 'The overridden deadline: enter a date and a time.');
      } else if (!why.ok) {
        refuse(why.code, 'dueOverride', why.message);
      } else {
        const o = overrideDue(due, at.value, why.value);
        if (o.ok) {
          computed[DUE_AT] = o.dueAt;
          columnNotes[DUE_AT] = o.note;
        } else if (o.code === 'no-deadline') {
          refuse('no-deadline', 'dueOverride', 'There is no computed deadline to override for this decision.');
        } else if (o.code === 'reason-required') {
          refuse('reason-required', 'dueOverride', 'Say why the deadline is different (at least 10 characters).');
        } else {
          refuse('invalid-date', 'dueOverride', 'The overridden deadline: enter a date and a time.');
        }
      }
    } else if (due.dueAt !== null) {
      computed[DUE_AT] = due.dueAt;
    } else if (after(DECISION) === NOT_REPORTABLE_DECISION && due.regimes.length === 0) {
      // Not reportable: an earlier deadline is cleared (the reason is on the row). Pending keeps whatever is on the
      // record (D-13-27-13), and a reportable decision still missing AwareAt is refused for AwareAt instead.
      computed[DUE_AT] = null;
    }
  }
  for (const column of columns) {
    if (column in accepted) proposed[column] = accepted[column];
    else if (column in computed) proposed[column] = computed[column];
    else if (changesColumns && column in stamps) proposed[column] = stamps[column] === 'now' ? toInstant(now) : actorUpn;
    else if (changesColumns && column === STATUS && verb === 'triage' && row.fields[STATUS] === TRIAGE_STATUS[register].from) {
      proposed[column] = TRIAGE_STATUS[register].to;
    }
  }
  const { patch, changes } = diffRecord(row, proposed);

  // --- Pass 4: required on the merged record (row ∪ set), only when a column is written -----------------------
  if (changesColumns) {
    const merged = (c: string): unknown => (c in patch ? patch[c] : row.fields[c]);
    for (const column of columns) {
      if (column === STATUS || bad.has(column)) continue;
      const spec = fieldOf(register, column) as FieldSpec;
      if (spec.kind === 'attachments') continue;
      if (deciding && column === DECISION) {
        // 13-27 (D-13-27-4): every save states the decision, chosen in this save (nothing is pre-selected), and
        // `Pending` chosen with a reason is an answer: "still deciding" is said, never implied by the default.
        if (!given.has(DECISION) || isBlank(given.get(DECISION))) {
          refuse('required', column, `Choose the ${spec.label.toLowerCase()}. Pending is an answer too, with your reason.`);
        }
        continue;
      }
      const rule = ruleFor(verb, spec, own);
      if (!holds(register, rule, merged, bad) || !isUnanswered(register, column, merged(column))) continue;
      const when = typeof rule === 'object' ? ` when ${describeRule(register, rule)}` : '';
      refuse('required', column, REQUIRED_WORDS[register]?.[verb]?.[column] ?? `${spec.label} is required${when}.`);
    }
    // 13-27 (D-13-27-5): the notification verb's per-notice rules, on the merged record.
    if (verb === 'notification') {
      const said = new Set(refusals.map((r) => r.column));
      for (const [notice, companions] of Object.entries(NOTICE_COMPANIONS[register] ?? {})) {
        if (bad.has(notice) || isBlank(merged(notice))) continue;
        const noticeLabel = fieldOf(register, notice)?.label ?? notice;
        for (const column of companions) {
          if (bad.has(column) || said.has(column) || !isUnanswered(register, column, merged(column))) continue;
          said.add(column);
          refuse('required', column, `${fieldOf(register, column)?.label ?? column} is required with the ${noticeLabel.replace(/ given at.*$/, '')}.`);
        }
      }
    }
  }

  // --- Rows ------------------------------------------------------------------------------------------------------
  const rowDrafts: PendingDraft[] = [];
  (input.rows ?? []).forEach((rowInput, i) => {
    const column = `rows.${i}`;
    const r = buildRow(rowVerb, row, rowInput, column, actorUpn, actor, now);
    if (!r.ok) {
      refuse(r.code, column, r.message);
      return;
    }
    rowDrafts.push(r.draft);
    if (r.warning) warnings.push(r.warning);
  });

  // --- 13.2-03 (D-13.2-P-4): triage refuses while the report is incomplete ----------------------------------------
  // Read on the record as it will be after this save (triage may add the client id itself), and in one refusal that
  // names what to add, in field-set order. A column already refused above is not named twice.
  if (verb === 'triage') {
    const said = new Set(refusals.map((r) => r.column));
    const missing = missingLodgeColumns({ ...row, fields: { ...row.fields, ...patch } }).filter((spec) => !said.has(spec.name));
    if (missing.length > 0) refuse('report-incomplete', undefined, `Complete the report first: ${missing.map((s) => s.label).join('; ')}`);
  }

  if (refusals.length > 0) return { ok: false, refusals, warnings };
  if (changes.length === 0 && rowDrafts.length === 0) {
    return { ok: false, refusals: [{ code: 'no-change', message: 'Nothing has changed, so there is nothing to save.' }], warnings };
  }

  // --- The drafts: one StateChange per changed column, then the rows; tagged as one save ------------------------
  const ctx: DraftContext = {
    verb,
    label: VERB_LABELS[verb],
    actorUpn,
    ...(actor.siteUserId ? { actorLookupId: actor.siteUserId } : {}),
    now,
    ...(reason ? { reason } : {}),
    ...(Object.keys(columnNotes).length > 0 ? { columnNotes } : {}),
  };
  const siblings = finaliseDrafts([...draftStateChanges(row, changes, ctx), ...rowDrafts], input.auditId);
  return { ok: true, patch, siblings, warnings };
}

// ---------------------------------------------------------------------------
// 13.2-03: complete (D-13.2-P-3)
// ---------------------------------------------------------------------------

/**
 * The CM completes an incident's report after lodge, intake stubs included. The PATCH holds only the columns that
 * change, with one `StateChange` sibling each; no Status, no stamp, no row. Partial saves are allowed (no required
 * rule is applied), but a lodge column that is always required and holds a value can't be blanked. Values are cleaned
 * by the lodge's own `cleanLodgeValue`, and the lodge's cross-field rules (only-when, `ReportedOn` not before
 * `OccurredOn`) and narrative warnings are checked on the record as it will be after the save, so a completed report
 * and a lodged one can never disagree. Every refusal comes back in one pass.
 */
function buildComplete(row: RecordRow, actor: RecordActor, input: RecordVerbInput, now: Date): RecordVerbBuild {
  const register = row.register;
  const refusals: Refusal[] = [];
  const warnings: Refusal[] = [];
  const refuse = (code: RecordRefusalCode, column: string | undefined, message: string) =>
    refusals.push(column === undefined ? { code, message } : { code, column, message });
  const labelOf = (column: string) => fieldOf(register, column)?.label ?? column;

  // --- The actor, the record, the inputs complete does not take ----------------------------------------------------
  const actorCheck = checkUpn(actor?.upn as string);
  if (!actorCheck.ok) refuse('invalid-actor', undefined, 'The signed-in account has no usable email address.');
  const actorUpn = actorCheck.ok ? actorCheck.value : '';

  const status = row.fields[STATUS];
  if (typeof status === 'string' && (CLOSED_STATUSES[register] ?? []).includes(status)) {
    refuse('record-closed', undefined, `This incident is ${status}, so its report can't be changed. Reopen it first.`);
  }

  let reason = '';
  if (input.reason !== undefined && input.reason !== null) {
    const r = cleanNoteText(input.reason, 'The reason');
    if (r.ok) reason = r.value;
    else refuse(r.code, 'reason', r.message);
  }
  if (input.dueOverride !== undefined) {
    refuse('not-applicable', 'dueOverride', 'A notification deadline is overridden only with the reportability decision or the WHS answer.');
  }
  if ((input.rows ?? []).length > 0) {
    refuse('not-applicable', 'rows', 'Completing the report adds no log entries. Add them where they are recorded: triage, WHS, notification or handling.');
  }

  // --- Pass 1: every key in set ----------------------------------------------------------------------------------
  const columns = verbColumns(register, 'complete');
  const own = new Set(columns);
  const provenance = PROVENANCE_LOCKED[register] ?? {};
  const clearOnly = CLEAR_ONLY[register] ?? {};
  const given = new Map<string, RecordFieldValue>();
  const bad = new Set<string>();
  for (const [key, raw] of Object.entries(input.set ?? {})) {
    if (raw === undefined) continue;
    if (key in provenance) {
      refuse('provenance-locked', key, provenance[key]);
      continue;
    }
    if (key === STATUS) {
      refuse('status-via-closure', key, 'Status moves only through triage (New to Actioning) and the closure verbs.');
      continue;
    }
    if (!own.has(key)) {
      refuse('not-this-verb', key, `${labelOf(key)} isn't part of the report, so it can't be changed here.`);
      continue;
    }
    const spec = fieldOf(register, key);
    if (!spec) throw new Error(`recordVerbs: ${register} complete column ${key} has no field spec`);
    if (key in clearOnly) {
      if (isBlank(raw)) given.set(key, null);
      else {
        refuse('clear-only', key, `${spec.label} ${clearOnly[key]}`);
        bad.add(key);
      }
      continue;
    }
    const r = cleanLodgeValue(register, spec, raw, now);
    if (r.kind === 'refused') {
      // The lodge's codes are a subset of this engine's (invalid-*, in-future, too-long, not-text, unknown-choice, …).
      refuse(r.code as RecordRefusalCode, key, r.message);
      bad.add(key);
      continue;
    }
    if (r.kind === 'value' && r.warning) warnings.push(r.warning);
    given.set(key, r.kind === 'value' ? r.value : null);
  }

  // --- Pass 2: what changes, and the reconcile key and blanking rules -----------------------------------------------
  const proposed: Record<string, RecordFieldValue> = {};
  for (const column of columns) if (given.has(column)) proposed[column] = given.get(column) as RecordFieldValue;
  const first = diffRecord(row, proposed);
  const lock = INTAKE_LOCKED[register];
  const intake = lock !== undefined && !isBlank(row.fields[lock.key]);
  const accepted: Record<string, RecordFieldValue> = {};
  for (const ch of first.changes) {
    const spec = fieldOf(register, ch.column) as FieldSpec;
    if (intake && ch.column === lock?.column) {
      refuse('reported-on-locked', ch.column, `${spec.label} can't be changed on a ShiftCare report: ShiftCare's report time ties this record to the daily reconcile.`);
      bad.add(ch.column);
      continue;
    }
    if (ch.cleared && spec.required.lodge === 'always') {
      refuse('cannot-blank-required', ch.column, `${spec.label} is part of the report and already has an answer, so it can't be blanked. Correct it instead.`);
      bad.add(ch.column);
      continue;
    }
    accepted[ch.column] = first.patch[ch.column];
  }
  const { patch, changes } = diffRecord(row, accepted);

  // --- Pass 3: the lodge's cross-field rules, on the record after the save ------------------------------------------
  const merged = (c: string): unknown => (c in patch ? patch[c] : row.fields[c]);
  const touched = new Set(Object.keys(patch));
  for (const column of lodgeConditions(register).onlyWhen) {
    const rule = fieldOf(register, column)?.required.lodge;
    if (!own.has(column) || bad.has(column) || typeof rule !== 'object' || bad.has(rule.when)) continue;
    if (!touched.has(column) && !touched.has(rule.when)) continue; // only what this save changes
    if (isBlank(merged(column)) || holds(register, rule, merged, bad)) continue;
    refuse('not-applicable', column, `Leave ${labelOf(column)} blank unless ${describeRule(register, rule)}.`);
  }
  if (own.has(REPORTED_ON) && !bad.has(OCCURRED_ON) && !bad.has(REPORTED_ON) && (touched.has(OCCURRED_ON) || touched.has(REPORTED_ON))) {
    const occurred = merged(OCCURRED_ON);
    const reported = merged(REPORTED_ON);
    if (typeof occurred === 'string' && typeof reported === 'string' && reportedBeforeOccurred(occurred, reported)) {
      if (touched.has(REPORTED_ON)) refuse('before-occurred', REPORTED_ON, `${labelOf(REPORTED_ON)} can't be before the incident happened.`);
      else refuse('before-occurred', OCCURRED_ON, `${labelOf(OCCURRED_ON)} can't be after ${labelOf(REPORTED_ON)}.`);
    }
  }
  for (const ch of changes) {
    const warning = ch.cleared ? undefined : contactWarning(register, ch.column, ch.newValue);
    if (warning) warnings.push(warning);
  }

  if (refusals.length > 0) return { ok: false, refusals, warnings };
  if (changes.length === 0) {
    return { ok: false, refusals: [{ code: 'no-change', message: 'Nothing has changed, so there is nothing to save.' }], warnings };
  }

  // --- The drafts: one StateChange per changed column, tagged as one save --------------------------------------------
  const ctx: DraftContext = {
    verb: 'complete',
    label: VERB_LABELS.complete,
    actorUpn,
    ...(actor.siteUserId ? { actorLookupId: actor.siteUserId } : {}),
    now,
    ...(reason ? { reason } : {}),
  };
  return { ok: true, patch, siblings: finaliseDrafts(draftStateChanges(row, changes, ctx), input.auditId), warnings };
}
